import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, History } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatDateTime } from '@/lib/format';
import { Card } from './Card';
import { Skeleton } from './Feedback';

/** Mirrors ActivityLogger::SUBJECTS on the API. */
export type ActivitySubject =
  | 'person'
  | 'company'
  | 'project'
  | 'tender'
  | 'prospect'
  | 'tool'
  | 'inventory-item'
  | 'certification'
  | 'certification-type'
  | 'induction'
  | 'evacuation'
  | 'ticket'
  | 'compliance-document'
  | 'site-diary'
  | 'defect';

export interface ActivityRow {
  id: string;
  action: string | null;
  description: string;
  occurred_at: string | null;
  is_recent: boolean;
  actor: string | null;
  /**
   * Somebody did this and the record of who was never kept.
   *
   * Only ever true of entries written before the log captured an actor — a
   * certification approved through the old hand-written path. A blank name on
   * its own invites the wrong conclusion: that nobody approved it, or that a
   * name was removed.
   */
  actor_unknown?: boolean;
  /**
   * What changed: the field, and both values. `from` is null on a creation and
   * `to` is null on a removal, so one side is the whole record.
   */
  changes?: { field: string; label: string; from: string | null; to: string | null }[];
}

/**
 * One line per field, in the one place both the record log and the module feed
 * read from — they rendered this separately and could drift.
 *
 * A creation has no "from" and a removal has no "to", so a one-sided change
 * reads as a plain value rather than an arrow pointing at nothing.
 */
export function ChangeList({ changes }: { changes: NonNullable<ActivityRow['changes']> }) {
  return (
    <ul className="mt-1 space-y-0.5">
      {changes.map((change, index) => (
        <li key={`${change.field}-${index}`} className="text-helper text-muted">
          <span className="font-semibold text-body">{change.label}:</span>{' '}
          {change.from === null || change.from === undefined ? (
            <span className="text-body">{change.to ?? '—'}</span>
          ) : change.to === null || change.to === undefined ? (
            <span className="line-through">{change.from}</span>
          ) : (
            <>
              <span className="line-through">{change.from}</span> <span aria-hidden>&rarr;</span>{' '}
              <span className="text-body">{change.to}</span>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Colour by what happened, so a deletion is findable in a long list without
 * reading every line. Anything unrecognised stays neutral rather than guessing.
 */
const ACTION_TONE: Record<string, string> = {
  created: 'bg-signal',
  updated: 'bg-digital',
  published: 'bg-signal',
  moved: 'bg-digital',
  started: 'bg-gold-500',
  ended: 'bg-navy',
  archived: 'bg-gold-500',
  deactivated: 'bg-gold-500',
  deleted: 'bg-danger',
};

export function useActivity(subject: ActivitySubject, id: string | undefined, limit = 30, enabled = true) {
  return useQuery({
    queryKey: ['activity', subject, id, limit],
    queryFn: () => api.get<ActivityRow[]>(`/activity/${subject}/${id}`, { query: { limit } }),
    enabled: enabled && Boolean(id),
  });
}

/**
 * The history of one record.
 *
 * One component for every module — a project, a tool and a roll-call all answer
 * "what happened to this" in the same shape, so they should all look the same
 * answering it.
 */
export function ActivityLog({
  subject,
  id,
  title = 'Activity',
  limit = 30,
  className,
  /** Render bare, for a page that already provides its own card. */
  bare = false,
  /**
   * The quiet strip at the foot of a page: closed until asked for, and it
   * fetches nothing until then. Client round 6 — "available on the page, but
   * not a standout".
   */
  collapsible = false,
}: {
  subject: ActivitySubject;
  id: string | undefined;
  title?: string;
  limit?: number;
  className?: string;
  bare?: boolean;
  collapsible?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { data, isPending, isError } = useActivity(subject, id, limit, !collapsible || open);
  const rows = data ?? [];

  const body = (
    <>
      {!collapsible && (
        <h2 className="flex items-center gap-2 text-card-title text-ink">
          <History aria-hidden className="h-4 w-4 text-muted" />
          {title}
        </h2>
      )}

      {isPending ? (
        <div className="mt-3 space-y-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-10 rounded" />
          ))}
        </div>
      ) : isError ? (
        <p className="mt-2 text-helper text-muted">The history could not be loaded.</p>
      ) : rows.length === 0 ? (
        <p className="mt-2 text-helper text-muted">
          Nothing recorded yet. Changes made from here on will be listed.
        </p>
      ) : (
        <ol className={cn(!collapsible && "mt-3")}>
          {rows.map((row, index) => (
            <li key={row.id} className="flex gap-3">
              {/* A rail rather than a bullet per row — it reads as one thread. */}
              <div className="flex flex-col items-center">
                <span
                  aria-hidden
                  className={cn(
                    'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                    ACTION_TONE[row.action ?? ''] ?? 'bg-muted',
                  )}
                />
                {index < rows.length - 1 && <span aria-hidden className="w-px flex-1 bg-line" />}
              </div>

              <div className={cn('min-w-0 flex-1', index < rows.length - 1 && 'pb-4')}>
                <p className="text-sm text-body">{row.description}</p>
                {(row.changes?.length ?? 0) > 0 && (
                  <ChangeList changes={row.changes!} />
                )}
                <p className="text-helper text-muted">
                  {row.occurred_at ? formatDateTime(row.occurred_at) : '—'}
                  {row.actor ? ` · ${row.actor}` : row.actor_unknown ? ' · recorded before this was tracked' : ''}
                  {row.is_recent && <span className="ml-2 text-signal-700">Recent</span>}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </>
  );

  if (collapsible) {
    return (
      <Card padded={false} className={cn('px-5 py-3', className)}>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex w-full items-center gap-2 text-left text-helper font-semibold text-muted hover:text-ink"
        >
          <History aria-hidden className="h-4 w-4" />
          <span className="flex-1">{title === 'Activity' ? 'Activity log' : title}</span>
          <span className="font-normal">{open ? 'Hide' : 'Show'}</span>
          <ChevronDown aria-hidden className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </button>
        {open && <div className="pb-2 pt-3">{body}</div>}
      </Card>
    );
  }

  if (bare) return <div className={className}>{body}</div>;

  return <Card className={className}>{body}</Card>;
}
